import { X } from "lucide-react";
import { useState } from "react";

import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";
import { IgSelect } from "@intelligems/ig-theme/IgSelect";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import type { ComponentPlacementLocation } from "@intelligems/ig-types/dtos/components";
import type {
  UpsellCondition,
  UpsellNumericOperator,
  UpsellPriceOperator,
} from "@intelligems/ig-types/schemas/visual-builder/onsite-upsell-rules";
import { InputAdornment, MenuItem } from "@mui/material";
import Stack from "@mui/material/Stack";
import { useDispatch } from "@src/store";
import { openOnsiteUpsellCartVariantsProductPicker } from "@src/store/slices/productPicker/thunks/onsiteUpsell/openOnsiteUpsellCartVariantsProductPicker";
import { openOnsiteUpsellPdpProductsProductPicker } from "@src/store/slices/productPicker/thunks/onsiteUpsell/openOnsiteUpsellPdpProductsProductPicker";
import { useCurrency } from "@src/utils/useCurrency";
import { EntitySelect } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/EntitySelect";
import { EntityOption } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/entityOptions";
import { PickerButton } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/PickerButton";
import { SelectedProductNames } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/SelectedProductNames";
import {
  CONDITION_TYPES,
  ConditionType,
  createCondition,
  MEMBERSHIP_OPTIONS,
  NUMERIC_OPERATOR_OPTIONS,
  PRICE_OPERATOR_OPTIONS,
} from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/options";

// One IF row: a condition-type select plus the value editor that type needs.
// `condition === null` renders the always-on "anything" row.
export const ConditionRow = ({
  condition,
  location,
  ruleIndex,
  conditionIndex,
  collections,
  tags,
  onChange,
  onRemove,
  showRemove,
}: {
  condition: UpsellCondition | null;
  location: ComponentPlacementLocation;
  ruleIndex: number;
  conditionIndex: number;
  collections: EntityOption[];
  tags: EntityOption[];
  onChange: (next: UpsellCondition | null) => void;
  onRemove: () => void;
  showRemove: boolean;
}) => {
  const dispatch = useDispatch();
  const { symbol } = useCurrency();
  const [expanded, setExpanded] = useState(false);
  const type: ConditionType = condition?.type ?? "anything";
  const options = CONDITION_TYPES[location];

  const isCurrency = type === "cartSubtotal" || type === "productPrice";
  const isBetween =
    condition !== null &&
    "operator" in condition &&
    condition.operator === "between";

  // Applies a new operator to a numeric/price condition: price has no `between`,
  // `between` gains a `max` bound, and switching away from it drops that bound.
  const handleOperatorChange = (
    cond: Extract<UpsellCondition, { operator: string }>,
    operator: string
  ) => {
    if (cond.type === "productPrice") {
      onChange({ ...cond, operator: operator as UpsellPriceOperator });
      return;
    }
    if (operator === "between") {
      onChange({ ...cond, operator: "between", max: cond.value });
    } else {
      const { max: _drop, ...rest } = cond;
      onChange({ ...rest, operator: operator as UpsellNumericOperator });
    }
  };

  return (
    <Stack direction="row" alignItems="flex-start" spacing={1}>
      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
        <IgSelect<ConditionType>
          fullWidth
          size="small"
          value={type}
          onChange={(e) => onChange(createCondition(e.target.value))}
        >
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </MenuItem>
          ))}
        </IgSelect>

        {/* include / exclude + value; the selected list drops below the row */}
        {condition !== null && "include" in condition && (
          <>
            <Stack direction="row" spacing={1}>
              <IgSelect<string>
                size="small"
                value={condition.include ? "true" : "false"}
                onChange={(e) =>
                  onChange({ ...condition, include: e.target.value === "true" })
                }
                sx={{ minWidth: 90 }}
              >
                {MEMBERSHIP_OPTIONS[location].map((o) => (
                  <MenuItem key={String(o.value)} value={String(o.value)}>
                    {o.label}
                  </MenuItem>
                ))}
              </IgSelect>
              {condition.type === "collection" && (
                <EntitySelect
                  options={collections}
                  value={condition.collectionId}
                  onChange={(collectionId) =>
                    onChange({ ...condition, collectionId })
                  }
                  placeholder="Search collections"
                />
              )}
              {condition.type === "tag" && (
                <EntitySelect
                  options={tags}
                  value={condition.tag}
                  onChange={(tag) => onChange({ ...condition, tag })}
                  placeholder="Search tags"
                />
              )}
              {condition.type === "products" && (
                <PickerButton
                  count={condition.productIds.length}
                  expanded={expanded}
                  onEdit={() =>
                    void dispatch(
                      openOnsiteUpsellPdpProductsProductPicker({
                        ruleIndex,
                        conditionIndex,
                      })
                    )
                  }
                  onToggle={() => setExpanded((open) => !open)}
                />
              )}
              {condition.type === "variants" && (
                <PickerButton
                  count={
                    new Set(condition.variants.map((v) => v.productId)).size
                  }
                  expanded={expanded}
                  onEdit={() =>
                    void dispatch(
                      openOnsiteUpsellCartVariantsProductPicker({
                        ruleIndex,
                        conditionIndex,
                      })
                    )
                  }
                  onToggle={() => setExpanded((open) => !open)}
                />
              )}
            </Stack>
            {expanded && condition.type === "products" && (
              <SelectedProductNames productIds={condition.productIds} />
            )}
            {expanded && condition.type === "variants" && (
              <SelectedProductNames
                productIds={[
                  ...new Set(condition.variants.map((v) => v.productId)),
                ]}
              />
            )}
          </>
        )}

        {/* operator + number(s) for numeric conditions */}
        {condition !== null && "operator" in condition && (
          <Stack direction="row" spacing={1} alignItems="center">
            <IgSelect<string>
              size="small"
              value={condition.operator}
              onChange={(e) => handleOperatorChange(condition, e.target.value)}
              sx={{ minWidth: 140 }}
            >
              {(condition.type === "productPrice"
                ? PRICE_OPERATOR_OPTIONS
                : NUMERIC_OPERATOR_OPTIONS
              ).map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </IgSelect>
            <IgTextField
              size="small"
              type="number"
              value={String(condition.value)}
              onChange={(e) =>
                onChange({ ...condition, value: Number(e.target.value) })
              }
              slotProps={
                isCurrency
                  ? {
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            {symbol}
                          </InputAdornment>
                        ),
                      },
                    }
                  : undefined
              }
              sx={{ width: 110 }}
            />
            {isBetween && "max" in condition && (
              <>
                <IgTypography color="textPlaceholder">AND</IgTypography>
                <IgTextField
                  size="small"
                  type="number"
                  value={String(condition.max ?? "")}
                  onChange={(e) =>
                    onChange({ ...condition, max: Number(e.target.value) })
                  }
                  slotProps={
                    isCurrency
                      ? {
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                {symbol}
                              </InputAdornment>
                            ),
                          },
                        }
                      : undefined
                  }
                  sx={{ width: 110 }}
                />
              </>
            )}
          </Stack>
        )}
      </Stack>

      {showRemove && (
        <IgIconButton
          size="small"
          onClick={onRemove}
          aria-label="Remove condition"
        >
          <X size={16} />
        </IgIconButton>
      )}
    </Stack>
  );
};
